import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-trace-flag-control>` says about itself.
 *
 * An EXPIRED flag is not a flag. Trace flags expire, and a surface that lists an expired one beside
 * a live one is the reason somebody reproduces a problem three times and gets no log. So expiry is
 * computed, said in words on the row, and counted separately in the summary — never left as a date
 * the reader has to compare to today by eye.
 *
 * No flags at all is the DANGEROUS empty state, not a neutral one: it means nothing is being
 * recorded, which is exactly what somebody about to reproduce a problem needs to know. It is said in
 * those words.
 *
 * Editing happens in the grid, as the specification requires of any table this platform builds:
 * there is no separate edit form, and each change is raised on its own rather than accumulated
 * behind a Save — a trace flag is a small immediate act, and batching would invent a draft state for
 * a thing that has none.
 */
const docs: ComponentDocs = {
  aliases: ['trace flags', 'debug levels', 'logging levels', 'trace management', 'log capture'],

  answers: [
    'find out whether anything is being logged right now',
    'turn logging up for one person before reproducing a problem',
    'see which trace flags have expired',
    'stop a trace flag that is filling the log list',
  ],

  placements: ['appPage'],

  whenToUse:
    'Before reproducing a problem, and whenever the answer to "why is there no log" might be that ' +
    'nothing was recording.',

  insteadUse: [
    { name: 'LogList', when: 'the flags are set and the question is which log to read.' },
    {
      name: 'DataTable',
      when:
        'the rows are not trace flags. This one computes expiry against a clock and says what that ' +
        'means in words.',
    },
  ],

  inputs: [
    {
      name: 'flags',
      type: 'json',
      description:
        'The flags in force: `{ id, subject, subjectType?, level, expiresAt? }[]`. `expiresAt` absent ' +
        'means NO EXPIRY WAS STATED, which is not the same as "never" and is counted on its own.',
      default: [],
    },
    {
      name: 'now',
      attribute: 'now',
      type: 'datetime',
      description:
        'The instant expiry is judged against — an epoch number or a parseable timestamp. An INPUT ' +
        'rather than a call to the clock, so what this component says about expiry is a function of ' +
        'what it was given. Absent falls back to the real clock, which is what an ordinary placement ' +
        'wants.',
      default: 'the real clock',
    },
    {
      name: 'readonly',
      attribute: 'readonly',
      type: 'boolean',
      description:
        'Draw the levels and expiries as text rather than controls, for somebody who may look but not ' +
        'change. The remove control goes with them.',
      default: false,
    },
    {
      name: 'search',
      attribute: 'search',
      type: 'text',
      description: 'Narrows by subject, subject type or level.',
    },
    {
      name: 'sortKey',
      attribute: 'sort-key',
      type: 'text',
      options: ['subject', 'level', 'expiresAt'],
      description: 'Which column orders the rows.',
      default: 'subject',
    },
    {
      name: 'sortDirection',
      attribute: 'sort-direction',
      type: 'text',
      options: ['asc', 'desc'],
      description:
        'Which way. A flag with no stated expiry sorts last in both directions — it is not the soonest ' +
        'to go and not the furthest away, it is unknown.',
      default: 'asc',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say with no flags at all. The default states the consequence, not the absence.',
      default: 'No trace flags — nothing is being recorded.',
    },
  ],

  events: [
    {
      name: 'caos-trace-change',
      when: 'A level or an expiry is changed in the grid. Raised per change, never batched.',
      carries: '`{ id, level? , expiresAt? }` — only what moved.',
    },
    { name: 'caos-trace-remove', when: 'A row’s Remove control is pressed.', carries: '`{ id }`.' },
  ],

  state: [
    { name: 'active', type: 'CaosTraceFlag[]', description: 'The flags still recording.' },
    {
      name: 'expired',
      type: 'CaosTraceFlag[]',
      description: 'The flags that have stopped. Never counted with the active ones.',
    },
    { name: 'now', type: 'number', description: 'The instant expiry is being judged against.' },
    { name: 'visibleFlags', type: 'CaosTraceFlag[]', description: 'The flags surviving the search, in order.' },
  ],

  parts: [
    { part: 'control', is: 'The outer column.' },
    { part: 'summary', is: 'The "N recording · M expired" line.' },
    { part: 'search', is: 'The search box.' },
    { part: 'table', is: 'The composed data table.' },
    { part: 'level', is: 'A row’s level control.' },
    { part: 'expires', is: 'A row’s expiry control.' },
    { part: 'state', is: 'A row’s recording/expired text.' },
    { part: 'remove', is: 'A row’s remove control.', when: '`readonly` is not set.' },
  ],

  accessibility:
    'Every in-grid control carries an accessible name naming the flag it belongs to — "Logging level ' +
    'for jrivera (User)" — because a grid of unlabelled dropdowns is what a screen reader otherwise ' +
    'finds. Expiry is stated as WORDS on the row ("expired — not recording"), never as a date the ' +
    'reader must compare to today. The summary is a `role="status"` live region and, with no flags ' +
    'at all, says "Nothing is being recorded" rather than reporting an empty list neutrally.',

  examples: [
    {
      key: 'mixed',
      label: 'One recording, one expired',
      summary:
        'The distinction the component exists for. The expired flag says so in words on its own row ' +
        'and is counted apart in the summary, rather than sitting there looking live.',
      placement: {
        component: 'caos-trace-flag-control',
        inputs: {
          now: '2026-09-18T16:00:00Z',
          flags: [
            { id: 'tf1', subject: 'jrivera', subjectType: 'User', level: 'FINEST', expiresAt: '2026-09-18T18:00:00Z' },
            { id: 'tf2', subject: 'integration.user', subjectType: 'User', level: 'DEBUG', expiresAt: '2026-09-18T09:00:00Z' },
            { id: 'tf3', subject: 'BillingService', subjectType: 'Class', level: 'INFO' },
          ],
        },
      },
      stage: { needsWidth: 760 },
    },
    {
      key: 'none',
      label: 'Nothing is being recorded',
      summary:
        'The dangerous empty state, said as a consequence rather than as an absence — it is the ' +
        'answer to "why is there no log" that people otherwise spend an afternoon on.',
      placement: { component: 'caos-trace-flag-control', inputs: { flags: [] } },
      stage: { needsWidth: 760 },
    },
    {
      key: 'readonly',
      label: 'Look but not change',
      summary: 'The levels and expiries as text, and no remove control — for somebody without the permission.',
      placement: {
        component: 'caos-trace-flag-control',
        inputs: {
          readonly: true,
          now: '2026-09-18T16:00:00Z',
          flags: [
            { id: 'tf1', subject: 'jrivera', subjectType: 'User', level: 'FINEST', expiresAt: '2026-09-18T18:00:00Z' },
          ],
        },
      },
      stage: { needsWidth: 760 },
    },
  ],
};

export default docs;
